// Cinema / Film Noir landing page — monolithic iframe block
// 4 brand variants: noir / archi / tech / organic
// Uses html[data-theme] attribute pattern

export type SerifLandingBrand = 'noir' | 'archi' | 'tech' | 'organic'
export interface SerifLandingPageData { brand: SerifLandingBrand }
export const DEFAULT_SERIF_LANDING_DATA: SerifLandingPageData = { brand: 'noir' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const SERIF_LANDING_CSS = `
/* ============ THEME TOKENS ============ */
:root{
  --fm:'IBM Plex Mono',monospace;
  --ease:cubic-bezier(.22,.68,.16,1);
}
html[data-theme="noir"]{
  --bg:#0f0e0c; --ink:#efece4; --mut:#8f897c;
  --line:rgba(239,236,228,.16); --acc:#e0492f;
  --fd:'Cormorant Garamond',serif; --fb:'Jost',sans-serif;
  --w:300; --lh:.98; --ls:-.015em; --tt:none;
}
html[data-theme="archi"]{
  --bg:#e9e6de; --ink:#1c1a17; --mut:#7b766c;
  --line:rgba(28,26,23,.18); --acc:#c05a21;
  --fd:'Space Grotesk',sans-serif; --fb:'Inter',sans-serif;
  --w:500; --lh:1.02; --ls:.005em; --tt:uppercase;
}
html[data-theme="tech"]{
  --bg:#0b0d0b; --ink:#e7efe4; --mut:#6e7a6a;
  --line:rgba(231,239,228,.14); --acc:#c6f24e;
  --fd:'IBM Plex Mono',monospace; --fb:'Manrope',sans-serif;
  --w:500; --lh:1.05; --ls:-.03em; --tt:none;
}
html[data-theme="organic"]{
  --bg:#f4eee3; --ink:#2c2822; --mut:#8b8375;
  --line:rgba(44,40,34,.18); --acc:#6f7d54;
  --fd:'Fraunces',serif; --fb:'Karla',sans-serif;
  --w:350; --lh:1.02; --ls:-.01em; --tt:none;
}

/* ============ BASE ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--fb); font-size:16px; line-height:1.6;
  -webkit-font-smoothing:antialiased; overflow-x:hidden;
  transition:background-color .6s ease,color .6s ease;
}
::selection{background:var(--acc);color:var(--bg)}
a{color:inherit;text-decoration:none}
img{display:block;max-width:100%}
button{font:inherit;color:inherit;background:none;border:0;cursor:pointer}
.wrap{width:min(1400px,92vw);margin:0 auto}
.mono{font-family:var(--fm);font-size:11px;letter-spacing:.14em;text-transform:uppercase}
.mut{color:var(--mut)}
body,.hdr,.row,.cell,.tick,.stat,.sw,.fbar,.svc,.qsec,footer{
  transition:background-color .6s ease,color .6s ease,border-color .6s ease;
}

/* noise */
.noise{position:fixed;inset:0;z-index:200;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");}

/* cursor */
#cur,#curR{position:fixed;top:0;left:0;z-index:300;pointer-events:none;border-radius:50%;mix-blend-mode:difference}
#cur{width:8px;height:8px;background:#fff;transform:translate(-50%,-50%)}
#curR{width:36px;height:36px;border:1px solid #fff;transform:translate(-50%,-50%);transition:width .3s,height .3s,opacity .3s}
body.cur-hot #curR{width:64px;height:64px;opacity:.6}
@media (hover:none){#cur,#curR{display:none}}

/* ============ HEADER ============ */
.hdr{position:fixed;top:0;left:0;right:0;z-index:100;border-bottom:1px solid transparent}
.hdr.scrolled{border-color:var(--line);background:color-mix(in srgb,var(--bg) 82%,transparent);backdrop-filter:blur(10px)}
.hdr .wrap{display:flex;align-items:center;justify-content:space-between;padding:18px 0;gap:20px}
.logo{font-family:var(--fd);font-size:22px;font-weight:var(--w);letter-spacing:.02em;line-height:1;white-space:nowrap}
.blink{animation:blink 1.1s steps(1) infinite;color:var(--acc)}
@keyframes blink{50%{opacity:0}}
.nav{display:flex;gap:28px}
.nav a{font-family:var(--fm);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--mut);position:relative;padding:4px 0}
.nav a::after{content:"";position:absolute;left:0;bottom:0;height:1px;width:100%;background:var(--ink);transform:scaleX(0);transform-origin:right;transition:transform .45s var(--ease)}
.nav a:hover{color:var(--ink)}
.nav a:hover::after{transform:scaleX(1);transform-origin:left}
.sws{display:flex;gap:14px;align-items:center}
.sw{font-family:var(--fm);font-size:11px;letter-spacing:.12em;color:var(--mut);padding:8px 2px;position:relative}
.sw::after{content:"";position:absolute;left:0;right:0;bottom:0;height:1px;background:var(--acc);transform:scaleX(0);transition:transform .4s var(--ease)}
.sw:hover{color:var(--ink)}
.sw.on{color:var(--ink)}
.sw.on::after{transform:scaleX(1)}

/* ============ HERO ============ */
.hero{padding:150px 0 70px;position:relative}
.hero .wrap{display:grid;grid-template-columns:7fr 5fr;gap:5vw;align-items:end;position:relative}
.bigIdx{position:absolute;right:-2vw;top:-40px;font-family:var(--fd);font-weight:var(--w);font-size:clamp(140px,24vw,340px);line-height:1;color:transparent;-webkit-text-stroke:1px var(--ink);opacity:.14;pointer-events:none;user-select:none;transition:opacity .6s}
.kick{display:flex;align-items:center;gap:14px;color:var(--mut);margin-bottom:34px}
.kick::before{content:"";width:44px;height:1px;background:var(--acc);flex:none}
h1{font-family:var(--fd);font-weight:var(--w);text-transform:var(--tt);letter-spacing:var(--ls);line-height:var(--lh);font-size:clamp(40px,9.2vw,138px);overflow-wrap:break-word}
h1 em{font-style:italic;color:var(--acc);letter-spacing:0}
html[data-theme="tech"] h1 em{font-style:normal;color:var(--acc)}
.lm{display:block;overflow:hidden}
.lm>span{display:block;transform:translateY(112%);transition:transform 1.1s var(--ease)}
.lm:nth-child(2)>span{transition-delay:.1s}.lm:nth-child(3)>span{transition-delay:.2s}
.hero.on .lm>span{transform:none}
.heroLead{max-width:44ch;color:var(--mut);margin:34px 0 0;font-size:15px}
.heroMeta{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-top:44px;border-top:1px solid var(--line);padding-top:16px}
.scrollHint{display:flex;align-items:center;gap:10px}
.scrollHint i{display:block;width:1px;height:34px;background:var(--ink);overflow:hidden;position:relative}
.scrollHint i::after{content:"";position:absolute;inset:0;background:var(--acc);animation:drop 1.8s var(--ease) infinite}
@keyframes drop{0%{transform:translateY(-100%)}60%{transform:translateY(100%)}100%{transform:translateY(100%)}}
.heroFig{position:relative;min-width:0}
.heroFig .frame{aspect-ratio:4/5;overflow:hidden;position:relative}
.heroFig img{width:100%;height:100%;object-fit:cover;transition:opacity .5s ease;animation:kb 18s ease-in-out infinite alternate}
@keyframes kb{from{transform:scale(1) translate(0,0)}to{transform:scale(1.08) translate(-1.5%,1.5%)}}
.heroFig img.dim{opacity:0}
.heroFig .cap{display:flex;justify-content:space-between;gap:10px;margin-top:12px;color:var(--mut)}
.heroFig .frame::after{content:"";position:absolute;inset:0;border:1px solid var(--line);pointer-events:none}

/* ============ TICKER ============ */
.tick{border-block:1px solid var(--line);padding:16px 0;overflow:hidden}
.tk-track{display:flex;width:max-content;animation:tk 28s linear infinite}
.tick:hover .tk-track{animation-play-state:paused}
.tk-half{display:flex;gap:3rem;padding-right:3rem;align-items:center;white-space:nowrap}
.tk-half b{font-family:var(--fd);font-weight:var(--w);font-size:18px;letter-spacing:.06em;text-transform:var(--tt)}
.tk-half s{text-decoration:none;color:var(--acc);font-size:12px}
@keyframes tk{to{transform:translateX(-50%)}}

/* ============ SECTIONS ============ */
section{padding:110px 0}
.shead{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:60px;gap:20px;flex-wrap:wrap}
.shead h2{font-family:var(--fd);font-weight:var(--w);text-transform:var(--tt);letter-spacing:var(--ls);line-height:1.05;font-size:clamp(30px,5vw,64px)}
.shead .idx{color:var(--mut)}

/* manifesto */
.manGrid{display:grid;grid-template-columns:4fr 8fr;gap:5vw}
.manGrid .side{position:sticky;top:120px;align-self:start}
.statement{font-family:var(--fd);font-weight:var(--w);font-size:clamp(22px,3.1vw,42px);line-height:1.3;letter-spacing:var(--ls)}
.statement .hl{color:var(--acc);font-style:italic}
html[data-theme="archi"] .statement .hl,html[data-theme="tech"] .statement .hl{font-style:normal}
.stats{display:grid;grid-template-columns:repeat(4,1fr);border-top:1px solid var(--line);margin-top:80px}
.stat{padding:34px 26px 6px 0;border-right:1px solid var(--line)}
.stat:last-child{border-right:0}
.stat .num{font-family:var(--fd);font-weight:var(--w);font-size:clamp(40px,5vw,76px);line-height:1;display:block}
.stat .num sup{font-size:.35em;color:var(--acc)}
.stat .lbl{display:block;margin-top:10px;color:var(--mut)}

/* works */
.rows{border-top:1px solid var(--line)}
.row{display:grid;grid-template-columns:70px 1fr auto 90px 50px;align-items:center;gap:20px;padding:28px 10px;border-bottom:1px solid var(--line);position:relative}
.row .rn{color:var(--mut)}
.row .rt{font-family:var(--fd);font-weight:var(--w);text-transform:var(--tt);letter-spacing:var(--ls);font-size:clamp(20px,3vw,38px);line-height:1.1;transition:transform .45s var(--ease),color .3s;min-width:0;overflow-wrap:break-word}
.row .rc{color:var(--mut);white-space:nowrap}
.row .ry{color:var(--mut);text-align:right;white-space:nowrap}
.row .ra{font-size:20px;justify-self:end;transition:transform .45s var(--ease),color .3s}
.row:hover{background:color-mix(in srgb,var(--ink) 5%,transparent)}
.row:hover .rt{transform:translateX(14px);color:var(--acc)}
.row:hover .ra{transform:translateX(8px) rotate(-45deg);color:var(--acc)}
#wprev{position:fixed;top:0;left:0;width:min(320px,26vw);aspect-ratio:4/3;overflow:hidden;z-index:90;pointer-events:none;opacity:0;transform:translate(-50%,-50%) rotate(-5deg) scale(.9);transition:opacity .35s ease,transform .35s var(--ease);border:1px solid var(--line)}
#wprev.show{opacity:1;transform:translate(-50%,-50%) rotate(-2deg) scale(1)}
#wprev img{width:100%;height:100%;object-fit:cover}
@media (hover:none){#wprev{display:none}}

/* services */
.svcGrid{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--line)}
.svc{padding:44px 30px 44px 0;border-bottom:1px solid var(--line);display:grid;grid-template-columns:70px 1fr;gap:20px}
.svc:nth-child(even){padding-left:40px;border-left:1px solid var(--line)}
.svc .n{color:var(--acc)}
.svc h3{font-family:var(--fd);font-weight:var(--w);text-transform:var(--tt);letter-spacing:var(--ls);font-size:clamp(22px,2.4vw,32px);margin-bottom:12px}
.svc p{color:var(--mut);font-size:14px;max-width:40ch}
.svc .tags{margin-top:18px;display:flex;flex-wrap:wrap;gap:8px}
.svc .tags span{font-family:var(--fm);font-size:10px;letter-spacing:.12em;text-transform:uppercase;border:1px solid var(--line);padding:5px 10px;color:var(--mut)}

/* quote */
.qsec{border-block:1px solid var(--line)}
.qsec blockquote{font-family:var(--fd);font-weight:var(--w);font-style:italic;font-size:clamp(24px,4.6vw,60px);line-height:1.2;letter-spacing:var(--ls);max-width:24ch;overflow-wrap:break-word}
html[data-theme="archi"] .qsec blockquote,html[data-theme="tech"] .qsec blockquote{font-style:normal}
.qsec .who{margin-top:34px;color:var(--mut)}
.qsec .qm{font-family:var(--fd);font-size:clamp(70px,10vw,160px);line-height:.5;color:var(--acc);display:block;margin-bottom:30px}

/* studio */
.stuGrid{display:grid;grid-template-columns:6fr 6fr;gap:5vw;align-items:start}
.stuFig{aspect-ratio:4/3;overflow:hidden;position:relative;min-width:0}
.stuFig img{width:100%;height:100%;object-fit:cover;transition:opacity .5s ease;animation:kb 22s ease-in-out infinite alternate}
.stuFig img.dim{opacity:0}
.stuFig::after{content:"";position:absolute;inset:0;border:1px solid var(--line)}
.stuTxt p{color:var(--mut);max-width:48ch;margin-bottom:16px;font-size:15px}
.clients{margin-top:44px;border-top:1px solid var(--line)}
.clients li{display:flex;justify-content:space-between;gap:12px;padding:14px 4px;border-bottom:1px solid var(--line);list-style:none;font-family:var(--fm);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--mut)}
.clients li b{color:var(--ink);font-weight:400}

/* contact / footer */
.contact{padding:150px 0 90px}
.contact .big{font-family:var(--fd);font-weight:var(--w);text-transform:var(--tt);letter-spacing:var(--ls);line-height:1.05;font-size:clamp(34px,8vw,120px);display:inline-block;margin:26px 0 10px;max-width:100%;overflow-wrap:break-word;
  background:linear-gradient(currentColor,currentColor) left bottom / 0% 2px no-repeat;transition:background-size .7s var(--ease)}
.contact .big:hover{background-size:100% 2px}
.contact .big em{font-style:italic;color:var(--acc)}
html[data-theme="archi"] .contact .big em,html[data-theme="tech"] .contact .big em{font-style:normal}
.mail{font-family:var(--fm);font-size:clamp(14px,2vw,20px);letter-spacing:.08em;display:inline-block;margin-top:26px;border-bottom:1px solid var(--line);padding-bottom:6px;transition:border-color .4s,color .3s;overflow-wrap:anywhere}
.mail:hover{color:var(--acc);border-color:var(--acc)}
.fbar{border-top:1px solid var(--line);margin-top:90px;padding-top:22px;display:flex;justify-content:space-between;gap:16px;flex-wrap:wrap;color:var(--mut)}

/* reveal */
.rv{opacity:0;transform:translateY(28px);transition:opacity .9s ease var(--d,0s),transform .9s var(--ease) var(--d,0s)}
.rv.on{opacity:1;transform:none}

/* ============ RESPONSIVE ============ */
@media (max-width:1100px){
  .nav{gap:20px}
  .hero .wrap{gap:4vw}
}
@media (max-width:960px){
  .nav{display:none}
  .hero{padding:130px 0 60px}
  .hero .wrap{grid-template-columns:1fr;gap:44px}
  .heroFig{max-width:560px;width:100%}
  .bigIdx{top:auto;bottom:38%;right:0;font-size:clamp(110px,22vw,200px)}
  .manGrid{grid-template-columns:1fr;gap:24px}
  .manGrid .side{position:static}
  .stats{grid-template-columns:1fr 1fr;margin-top:56px}
  .stat{border-right:0;padding-left:0}
  .svcGrid{grid-template-columns:1fr}
  .svc:nth-child(even){padding-left:0;border-left:0}
  .stuGrid{grid-template-columns:1fr;gap:40px}
  .row{grid-template-columns:44px 1fr 64px 30px;gap:14px;padding:22px 6px}
  .row .rc{display:none}
}
@media (max-width:720px){
  body{font-size:15px}
  .wrap{width:90vw}
  section{padding:70px 0}
  .hdr .wrap{padding:14px 0;gap:12px}
  .logo{font-size:19px}
  .sws{gap:10px}
  .sw{padding:10px 2px;min-width:26px}
  .hero{padding:110px 0 50px}
  h1{font-size:clamp(34px,11.5vw,72px)}
  .kick{margin-bottom:24px;font-size:10px}
  .heroLead{margin-top:24px;font-size:14px}
  .heroMeta{margin-top:32px}
  .bigIdx{display:none}
  .heroFig .frame{aspect-ratio:4/4}
  .shead{flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:36px}
  .statement{font-size:clamp(20px,5.4vw,28px)}
  .stats{margin-top:44px}
  .stat{padding:24px 14px 24px 0}
  .stat:nth-child(even){padding-left:16px}
  .stat .lbl{font-size:10px}
  .row{grid-template-columns:34px 1fr 26px;gap:10px;padding:18px 2px}
  .row .ry{display:none}
  .row .rt{font-size:clamp(18px,4.6vw,24px)}
  .row .ra{font-size:16px}
  .svc{grid-template-columns:1fr;gap:8px;padding:32px 0}
  .svc .n{font-size:10px}
  .qsec{padding:70px 0}
  .qsec .qm{margin-bottom:20px}
  .qsec .who{margin-top:24px}
  .contact{padding:80px 0 60px}
  .contact .big{margin:18px 0 6px;font-size:clamp(30px,9vw,56px)}
  .mail{margin-top:18px;font-size:14px}
  .fbar{margin-top:56px;flex-direction:column;gap:10px}
}
@media (max-width:400px){
  h1{font-size:clamp(30px,10.5vw,40px)}
  .heroFig .frame{aspect-ratio:4/5}
  .stats{grid-template-columns:1fr}
  .stat:nth-child(even){padding-left:0}
  .sws{gap:6px}
}
@media (prefers-reduced-motion:reduce){
  *,*::before,*::after{animation:none!important;transition:none!important}
  html{scroll-behavior:auto}
  .rv{opacity:1;transform:none}
  .lm>span{transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const SERIF_LANDING_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>
<div id="cur" aria-hidden="true"></div><div id="curR" aria-hidden="true"></div>

<header class="hdr" id="hdr">
  <div class="wrap">
    <a class="logo" href="#top" id="logo">BST°</a>
    <nav class="nav" aria-label="Навигация">
      <a href="#manifest">Манифест</a>
      <a href="#works">Проекты</a>
      <a href="#studio">Студия</a>
      <a href="#contact">Контакт</a>
    </nav>
    <div class="sws" role="group" aria-label="Варианты бренда">
      <button class="sw on" data-k="noir" aria-pressed="true">01</button>
      <button class="sw" data-k="archi" aria-pressed="false">02</button>
      <button class="sw" data-k="tech" aria-pressed="false">03</button>
      <button class="sw" data-k="organic" aria-pressed="false">04</button>
    </div>
  </div>
</header>

<main id="top">
  <!-- HERO -->
  <section class="hero" id="hero">
    <div class="wrap">
      <div class="bigIdx" id="bigIdx" aria-hidden="true">01</div>
      <div>
        <p class="kick mono" id="heroKicker">BST° — дом моды и айдентика</p>
        <h1 id="heroTitle"></h1>
        <p class="heroLead rv" id="heroLead"></p>
        <div class="heroMeta mono">
          <span id="themeName">01 / Noir — fashion house</span>
          <span class="scrollHint"><i></i>scroll</span>
        </div>
      </div>
      <figure class="heroFig">
        <div class="frame"><img id="heroImg" alt="BST — визуальная кампания"></div>
        <figcaption class="cap mono"><span id="heroCap">Кампания №9</span><span>fig. 01</span></figcaption>
      </figure>
    </div>
  </section>

  <!-- TICKER -->
  <div class="tick" aria-hidden="true"><div class="tk-track" id="tkTrack"></div></div>

  <!-- MANIFESTO -->
  <section id="manifest">
    <div class="wrap manGrid">
      <div class="side"><p class="kick mono">01 — Манифест</p></div>
      <div>
        <p class="statement rv" data-t="maniStatement">Мы убираем лишнее, пока не остаётся суть. Бренд, интерфейс, кадр — деталь работает, когда ей не нужно кричать. <span class="hl">Тишина — наш самый громкий инструмент.</span></p>
        <div class="stats">
          <div class="stat rv"><span class="num"><span data-n="9" data-pad="2">00</span><sup>+</sup></span><span class="lbl mono" data-t="stat1L">лет практики</span></div>
          <div class="stat rv" style="--d:.1s"><span class="num"><span data-n="68">0</span></span><span class="lbl mono" data-t="stat2L">проектов</span></div>
          <div class="stat rv" style="--d:.2s"><span class="num"><span data-n="14">0</span></span><span class="lbl mono" data-t="stat3L">наград</span></div>
          <div class="stat rv" style="--d:.3s"><span class="num"><span data-n="6" data-pad="2">00</span></span><span class="lbl mono" data-t="stat4L">стран</span></div>
        </div>
      </div>
    </div>
  </section>

  <!-- WORKS -->
  <section id="works">
    <div class="wrap">
      <div class="shead rv"><h2>Избранные проекты</h2><span class="idx mono">02 — индекс</span></div>
      <div class="rows" id="worksList"></div>
    </div>
  </section>
  <div id="wprev" aria-hidden="true"><img alt=""></div>

  <!-- SERVICES -->
  <section id="services">
    <div class="wrap">
      <div class="shead rv"><h2>Компетенции</h2><span class="idx mono">03 — практика</span></div>
      <div class="svcGrid">
        <div class="svc rv"><span class="n mono">/01</span><div><h3 data-t="svc1Title">Айдентика</h3><p data-t="svc1Desc">Логотип, типографика, цвет и голос. Системы, которые выдерживают любой носитель — от бирки до фасада.</p><div class="tags"><span>логотип</span><span>гайдлайн</span><span>нейминг</span></div></div></div>
        <div class="svc rv" style="--d:.1s"><span class="n mono">/02</span><div><h3 data-t="svc2Title">Цифровой дизайн</h3><p data-t="svc2Desc">Сайты и интерфейсы, собранные как архитектура: сетка, ритм, воздух, скорость.</p><div class="tags"><span>website</span><span>ui-kit</span><span>design ops</span></div></div></div>
        <div class="svc rv" style="--d:.15s"><span class="n mono">/03</span><div><h3 data-t="svc3Title">Арт-дирекция</h3><p data-t="svc3Desc">Кампании, съёмки, редакционный дизайн. Кадр как продолжение бренда.</p><div class="tags"><span>кампания</span><span>editorial</span><span>съёмка</span></div></div></div>
        <div class="svc rv" style="--d:.2s"><span class="n mono">/04</span><div><h3 data-t="svc4Title">Стратегия</h3><p data-t="svc4Desc">Позиционирование и архитектура бренда до того, как нарисована первая линия.</p><div class="tags"><span>research</span><span>платформа</span><span>tone of voice</span></div></div></div>
      </div>
    </div>
  </section>

  <!-- QUOTE -->
  <section class="qsec">
    <div class="wrap">
      <span class="qm rv" aria-hidden="true">«</span>
      <blockquote class="rv" data-t="quoteText">Минимализм — это не меньше. Это ровно столько, сколько нужно.</blockquote>
      <p class="who mono rv" data-t="quoteWho" style="--d:.15s">— BST, внутреннее правило №1</p>
    </div>
  </section>

  <!-- STUDIO -->
  <section id="studio">
    <div class="wrap stuGrid">
      <figure class="stuFig rv"><img id="stuImg" alt="BST — студия, материал и свет"></figure>
      <div class="stuTxt">
        <div class="shead rv" style="margin-bottom:30px"><h2>Студия</h2><span class="idx mono">04 — бюро</span></div>
        <p class="rv" data-t="studioText1">BST — независимое дизайн-бюро. Небольшая команда старших дизайнеров, стратегов и арт-директоров. Берём мало проектов, ведём каждый лично.</p>
        <p class="rv" data-t="studioText2" style="--d:.1s">Работаем на стыке моды, архитектуры, технологий и предметного дизайна — поэтому один и тот же метод даёт четыре совершенно разных бренда.</p>
        <ul class="clients rv" style="--d:.15s">
          <li><b>AURUM</b><span>fashion — 2025</span></li>
          <li><b>HAUS B.</b><span>architecture — 2024</span></li>
          <li><b>KERNEL</b><span>technology — 2024</span></li>
          <li><b>SAGE&amp;CLAY</b><span>cosmetics — 2023</span></li>
          <li><b>TONO</b><span>gallery — 2023</span></li>
        </ul>
      </div>
    </div>
  </section>

  <!-- CONTACT -->
  <footer class="contact" id="contact">
    <div class="wrap">
      <p class="kick mono rv">05 — контакт</p>
      <a class="big rv" data-t="ctaTitle" href="mailto:hello@bst.studio">Начнём <em>тихий</em> проект</a><br>
      <a class="mail rv" style="--d:.1s" href="mailto:hello@bst.studio">hello@bst.studio</a>
      <div class="fbar mono">
        <span>© 2025 BST bureau</span>
        <span id="footTheme">вариант 01 — noir</span>
        <span>сделано тихо · москва — везде</span>
      </div>
    </div>
  </footer>
</main>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function serifLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= DATA ================= */
const IMG = {
  noir:{a:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/1b88fbb01-8bf0-48d7-9947-1601c64fe452.png",
        b:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/16a79f6e7-e572-43a8-b905-4be2874ba4d2.png"},
  archi:{a:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/1d5e2fbc9-730d-4f1d-9605-b6693def8ffa.png",
         b:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/16f443246-4282-41eb-9b64-e032aecff707.png"},
  tech:{a:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/1652e07cc-0d66-4b05-aee0-f4b7467674b5.png",
        b:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/1eb300ed8-6179-4ecf-b3e7-a2794b4056d9.png"},
  organic:{a:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/1b80eac49-d78e-4279-acd1-b33e27d3f9b5.png",
           b:"https://image.qwenlm.ai/public_source/6606845c-dec2-45a3-9e6b-7cfd3286e36f/18ce3cc24-f56f-48a5-b53f-0b0161a55fe2.png"}
};
const TH = {
  noir:{num:"01",name:"01 / Noir — fashion house",foot:"вариант 01 — noir",logo:"BST°",
    kicker:"BST° — дом моды и айдентика",
    lines:["Тишина —","новая","<em>роскошь.</em>"],
    lead:"Капсульные коллекции и айдентика для домов, которым не нужно кричать, чтобы их услышали.",
    cap:"Кампания №9 — шерсть, свет, тишина",
    tick:["BST°","NOIR","FASHION","IDENTITY","EDITORIAL","ATELIER","EST. 2016"],
    works:[["01","Noir No.9","мода · айдентика","2025"],["02","Silent Issue","editorial · print","2024"],["03","Atelier M.","ребрендинг дома","2024"],["04","Velvet Hours","кампания · арт-дирекция","2023"]]},
  archi:{num:"02",name:"02 / Archi — architecture bureau",foot:"вариант 02 — archi",logo:"BST/",
    kicker:"BST/ — архитектурное бюро",
    lines:["Порядок","как","<em>материал.</em>"],
    lead:"Проектируем павильоны и музеи, где свет, бетон и тишина работают как одна система.",
    cap:"Павильон 04 — бетон на рассвете",
    tick:["BST/","ARCHITECTURE","MUSEUMS","WAYFINDING","CONCRETE","LIGHT","EST. 2016"],
    works:[["01","Pavilion 04","объект · концепция","2025"],["02","Museum of Silence","экспозиция","2024"],["03","Haus B.","резиденция · навигация","2024"],["04","Dune Archive","айдентика · wayfinding","2023"]]},
  tech:{num:"03",name:"03 / Tech — systems studio",foot:"вариант 03 — tech",logo:'BST<span class="blink">_</span>',
    kicker:"BST_ — технологическая студия",
    lines:["Система","вместо","<em>шума.</em>"],
    lead:"Интерфейсы, железо и дизайн-системы для продуктов, которые говорят на языке точности.",
    cap:"Mono One — сигнальная линия",
    tick:["BST_","SYSTEMS","INTERFACES","HARDWARE","DESIGN OPS","SIGNAL","EST. 2016"],
    works:[["01","Kernel OS","продукт · интерфейс","2025"],["02","Signal Grid","dataviz · платформа","2024"],["03","Mono One","hardware · айдентика","2024"],["04","Pulse API","дизайн-система","2023"]]},
  organic:{num:"04",name:"04 / Organic — slow cosmetics",foot:"вариант 04 — organic",logo:"bst.",
    kicker:"bst. — органическая косметика",
    lines:["Природа","как","<em>метод.</em>"],
    lead:"Уходовая косметика и упаковка из простых материалов — тепло, медленно, честно.",
    cap:"Sage & Clay — утренний ритуал",
    tick:["bst.","organic","skincare","slow beauty","ceramics","field notes","est. 2016"],
    works:[["01","Sage & Clay","упаковка · айдентика","2025"],["02","Morning Ritual","продуктовая линия","2024"],["03","Field Notes","limited · print","2024"],["04","Bloom Bar","retail · пространство","2023"]]}
};
const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
const $ = s => document.querySelector(s);
let cur = "noir";

/* preload */
Object.values(IMG).forEach(o=>{new Image().src=o.a;new Image().src=o.b;});

/* scramble decode */
function scramble(el,text,dur=650){
  if(RM){el.textContent=text;return;}
  const chars="BST#/\\\\·—°[]<>0123456789",t0=performance.now();
  (function tick(t){
    const p=Math.min(1,(t-t0)/dur);let out="";
    for(let i=0;i<text.length;i++){out+= i/text.length<p ? text[i] : chars[(Math.random()*chars.length)|0];}
    el.textContent=out; if(p<1)requestAnimationFrame(tick);
  })(t0);
}

/* image crossfade */
function swapImg(el,src){
  if(el.dataset.src===src)return;
  el.classList.add("dim");
  const im=new Image();
  im.onload=()=>{el.src=src;el.dataset.src=src;requestAnimationFrame(()=>el.classList.remove("dim"));};
  im.src=src;
}

/* build heading with line masks */
function setHeading(lines){
  const h=$("#heroTitle");h.innerHTML="";
  lines.forEach(l=>{
    const outer=document.createElement("span");outer.className="lm";
    const inner=document.createElement("span");inner.innerHTML=l;
    outer.appendChild(inner);h.appendChild(outer);
  });
}

/* ticker */
function setTicker(items){
  const half=items.map(i=>\`<b>\${i}</b><s>✦</s>\`).join("");
  $("#tkTrack").innerHTML=\`<span class="tk-half">\${half}</span><span class="tk-half" aria-hidden="true">\${half}</span>\`;
}

/* works */
function setWorks(t){
  const list=$("#worksList");list.innerHTML="";
  t.works.forEach((w,i)=>{
    const a=document.createElement("a");
    a.className="row rv on";a.href="#contact";
    a.dataset.img = i%2===0 ? IMG[cur].a : IMG[cur].b;
    a.innerHTML=\`<span class="rn mono">\${w[0]}</span><span class="rt">\${w[1]}</span><span class="rc mono">\${w[2]}</span><span class="ry mono">\${w[3]}</span><span class="ra">→</span>\`;
    list.appendChild(a);
  });
}

/* apply theme */
function applyTheme(k,first=false){
  cur=k;const t=TH[k];
  document.documentElement.dataset.theme=k;
  $("#logo").innerHTML=t.logo;
  scramble($("#heroKicker"),t.kicker);
  setHeading(t.lines);
  requestAnimationFrame(()=>requestAnimationFrame(()=>$("#hero").classList.add("on")));
  $("#heroLead").textContent=t.lead;$("#heroLead").classList.add("on");
  $("#heroCap").textContent=t.cap;
  scramble($("#themeName"),t.name);
  $("#bigIdx").textContent=t.num;
  $("#footTheme").textContent=t.foot;
  swapImg($("#heroImg"),IMG[k].a);
  swapImg($("#stuImg"),IMG[k].b);
  setTicker(t.tick);
  setWorks(t);
  document.querySelectorAll(".sw").forEach(b=>{
    const on=b.dataset.k===k;b.classList.toggle("on",on);b.setAttribute("aria-pressed",on);
  });
  if(!first)document.title=\`BST \${t.num} — минимальное дизайн-бюро\`;
}

/* switcher */
document.querySelectorAll(".sw").forEach(b=>b.addEventListener("click",()=>applyTheme(b.dataset.k)));
addEventListener("keydown",e=>{
  const map={"1":"noir","2":"archi","3":"tech","4":"organic"};
  if(map[e.key])applyTheme(map[e.key]);
});

/* works hover preview */
const prev=$("#wprev"),pimg=prev.querySelector("img");
let tx=innerWidth/2,ty=innerHeight/2,px=tx,py=ty;
if(matchMedia("(hover:hover)").matches){
  $("#works").addEventListener("mousemove",e=>{tx=e.clientX;ty=e.clientY;});
  $("#worksList").addEventListener("mouseover",e=>{
    const r=e.target.closest(".row");if(!r)return;
    pimg.src=r.dataset.img;prev.classList.add("show");document.body.classList.add("cur-hot");
  });
  $("#worksList").addEventListener("mouseout",e=>{
    if(e.target.closest(".row")){prev.classList.remove("show");document.body.classList.remove("cur-hot");}
  });
  (function loop(){px+=(tx-px)*.12;py+=(ty-py)*.12;prev.style.left=px+"px";prev.style.top=py+"px";requestAnimationFrame(loop);})();
}

/* cursor */
if(matchMedia("(hover:hover)").matches && !RM){
  const c=$("#cur"),r=$("#curR");let mx=0,my=0,rx=0,ry=0;
  addEventListener("mousemove",e=>{mx=e.clientX;my=e.clientY;c.style.left=mx+"px";c.style.top=my+"px";});
  (function cl(){rx+=(mx-rx)*.16;ry+=(my-ry)*.16;r.style.left=rx+"px";r.style.top=ry+"px";requestAnimationFrame(cl);})();
  document.addEventListener("mouseover",e=>{if(e.target.closest("a,button"))document.body.classList.add("cur-hot");});
  document.addEventListener("mouseout",e=>{if(e.target.closest("a,button"))document.body.classList.remove("cur-hot");});
}else{$("#cur").style.display=$("#curR").style.display="none";}

/* header scrolled */
addEventListener("scroll",()=>$("#hdr").classList.toggle("scrolled",scrollY>40),{passive:true});

/* reveal on scroll */
const io=new IntersectionObserver(es=>es.forEach(en=>{if(en.isIntersecting){en.target.classList.add("on");io.unobserve(en.target);}}}),{threshold:.15});
document.querySelectorAll(".rv").forEach(el=>io.observe(el));

/* counters */
const cio=new IntersectionObserver(es=>es.forEach(en=>{
  if(!en.isIntersecting)return;cio.unobserve(en.target);
  const el=en.target,n=+el.dataset.n,pad=+(el.dataset.pad||0);
  const fmt=v=>String(v).padStart(pad,"0");
  if(RM){el.textContent=fmt(n);return;}
  const t0=performance.now();
  (function step(t){const p=Math.min(1,(t-t0)/1400),e=1-Math.pow(1-p,3);
    el.textContent=fmt(Math.round(n*e));if(p<1)requestAnimationFrame(step);})(t0);
}),{threshold:.6});
document.querySelectorAll("[data-n]").forEach(el=>cio.observe(el));

/* init */
applyTheme("${brand}",true);
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('[data-rv],.rv,.reveal').forEach(function(el){el.classList.add('in');el.classList.add('on');});
  document.querySelectorAll('.ln').forEach(function(el){
    var inner=el.querySelector('.ln-inner')||el;
    inner.style.transform='none';inner.style.opacity='1';
  });
  document.querySelectorAll('.lm>span').forEach(function(el){el.style.transform='none';});
  document.getElementById('hero').classList.add('on');
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getSerifLandingPageHtml(data: SerifLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'noir'
  return `<!DOCTYPE html>
<html lang="ru" data-theme="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/300.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/cormorant-garamond/500-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jost/300.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jost/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jost/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/inter/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/inter/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/300.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/500-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/karla/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/karla/500.css">
<style>${SERIF_LANDING_CSS}</style>
</head>
<body>
${SERIF_LANDING_HTML_BODY}
<script>${serifLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
